import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import LoadingModal from './LoadingModal';
import TableContainer from '../TableContainer';
import Pagination10 from '../Pagination10';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import ConfirmModal from './ConfirmModal';
import DefaultModal from './DefaultModal';
import ResponsiveShadow from '../ResponsiveShadow';
import usePagination10 from '../hooks/usePagination10';
import useParametrosEntidade from '../hooks/useParametrosEntidade';
import PageHeader from '../PageHeader';
import PageContent from '../PageContent';
import ContratoPedidoModal from './ContratoPedidoModal';
import ContratoPedidoItensModal from './ContratoPedidoItensModal';
import PdfViewerModal from './PdfViewerModal';
import PreEmpenhoModal from './PreEmpenhoModal';
import ConfirmarEntregaModal from './ConfirmarEntregaModal';
import ReverterEntregaModal from './ReverterEntregaModal';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';
import { Dropdown } from 'react-bootstrap';
import useAsyncPdf from '../hooks/useAsyncPdf';

function ContratoPedidosModal({ isOpen, onClose, contrato, possuiAditivo }) {
  const [contratoPedidos, setContratoPedidos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [contratoPedidoSelecionado, setContratoPedidoSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [showReversaoModal, setShowReversaoModal] = useState(false);
  const [isReversando, setReversando] = useState(false);
  const [reversaoItens, setReversaoItens] = useState([]);
  const [confirmacaoItens, setConfirmacaoItens] = useState([]);
  const [nfData, setNfData] = useState({ numNotaFiscal: '', serieNotaFiscal: '', dataNotaFiscal: '', valorNotaFiscal: '' });
  const [modalContratoPedidoItensAberto, setModalContratoPedidoItensAberto] = useState(false);
  const [contratoPedidoItemSelecionado, setContratoPedidoItemSelecionado] = useState(null);
  const [showPreEmpenhoModal, setShowPreEmpenhoModal] = useState(false);
  const [preEmpenhoData, setPreEmpenhoData] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'ADMIN';
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { params: paramsEntidade } = useParametrosEntidade(contrato?.idEntidade);
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const importInputRef = useRef(null);
  const generatePdf = useAsyncPdf({ token });

  useEffect(() => {
    if (contrato && contrato.id) {
      carregarPedidos(contrato.id);
    }
  }, [contrato, atualizar]);

  function carregarPedidos(idContrato) {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.CONTRATO_PEDIDO}/contrato/${idContrato}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setContratoPedidos(result.data))
      .catch(error => {
        console.error('Erro ao obter pedidos do contrato:', error);
        showDefaultModal('error', 'Erro ao carregar pedidos do contrato.');
      })
      .finally(() => setLoading(false));
  }

  function handleDelete(contratoPedido) {
    const pedidosPosteriores = contratoPedidos.filter(p => p.id > contratoPedido.id);
    if (pedidosPosteriores.length > 0) {
      showDefaultModal('error', 'Não é possível excluir este pedido pois já existem pedidos posteriores.');
      return;
    }
    if (contratoPedido.status && contratoPedido.status !== 'PENDENTE') {
      showDefaultModal('error', 'Não é possível excluir este pedido pois já possui entrega registrada.');
      return;
    }
    showConfirmModal('delete', () => handleConfirmDelete(contratoPedido));
  }

  function handleConfirmDelete(contratoPedido) {
    axios
      .delete(`${API_BASE_URL.CONTRATO_PEDIDO}/${contrato.id}/${contratoPedido.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        showDefaultModal('success', 'Pedido excluído com sucesso!');
        setAtualizar(prev => !prev);
      })
      .catch(error => {
        console.error('Erro ao excluir pedido:', error);
        showDefaultModal('error', 'Erro ao excluir o pedido!');
      });
  }

  function handleEdit(contratoPedido) {
    setContratoPedidoSelecionado(contratoPedido);
    setShowModal(true);
  }

  function handleNew() {
    let dataUltimoPedido = null;
    if (contratoPedidos.length > 0) {
      const pedidosOrdenados = [...contratoPedidos].sort((a, b) => new Date(a.data) - new Date(b.data));
      const ultimoPedido = pedidosOrdenados[pedidosOrdenados.length - 1];
      dataUltimoPedido = ultimoPedido.data;
      if (!ultimoPedido.itens || ultimoPedido.itens.length === 0) {
        showDefaultModal('error', `O último pedido (nº ${ultimoPedido.id}) não possui itens registrados. Registre os itens antes de criar um novo pedido.`);
        return;
      }
    }
    setContratoPedidoSelecionado({ dataUltimoPedido });
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setContratoPedidoSelecionado(null);
  }

  function handleSaveSuccess(novoPedido) {
    setAtualizar(prev => !prev);
    setContratoPedidoSelecionado(null);
    if (novoPedido?.id) {
      setContratoPedidoItemSelecionado(novoPedido);
      setModalContratoPedidoItensAberto(true);
    }
  }

  function getBadgeStatus(status) {
    const map = {
      PENDENTE: 'bg-warning text-dark',
      ENTREGUE_PARCIAL: 'bg-info text-dark',
      ENTREGUE_TOTAL: 'bg-success',
      CANCELADO: 'bg-danger',
    };
    return map[status] || 'bg-secondary';
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const formatDate = date => {
    if (!date) return '';
    if (Array.isArray(date)) {
      const [year, month, day] = date;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const contratoPedidosFiltrados = Array.isArray(contratoPedidos)
    ? contratoPedidos
        .filter(pedido => {
          const normalizedBusca = normalizeString(filtro);
          return (
            normalizeString(pedido.id?.toString() || '').includes(normalizedBusca) ||
            normalizeString(formatDate(pedido.data) || '').includes(normalizedBusca)
          );
        })
        .sort((a, b) => b.id - a.id)
    : [];

  const totalItems = contratoPedidosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const currentItems = contratoPedidosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const calcularValorTotal = (pedido) => {
    if (!pedido.itens || !Array.isArray(pedido.itens)) return 0;
    return pedido.itens.reduce((total, item) => {
      return total + ((item.qtdePedida || 0) * (item.contratoItem?.valorUnit || 0));
    }, 0);
  };

  const calcularValorEntregue = (pedido) => {
    if (!pedido.itens || !Array.isArray(pedido.itens)) return 0;
    return pedido.itens.reduce((total, item) => {
      return total + ((item.qtdeEntregue || 0) * (item.contratoItem?.valorUnit || 0));
    }, 0);
  };

  const calcularTotalGeral = () => {
    return contratoPedidosFiltrados.reduce((total, pedido) => {
      return total + calcularValorTotal(pedido);
    }, 0);
  };

  const calcularTotalGeralEntregue = () => {
    return contratoPedidosFiltrados.reduce((total, pedido) => {
      return total + calcularValorEntregue(pedido);
    }, 0);
  };

  const calcularTotalGeralPendente = () => {
    return calcularTotalGeral() - calcularTotalGeralEntregue();
  };

  const possuiPedidoPosterior = (pedido) => {
    if (!pedido) return false;
    return contratoPedidos.some(p => p.id > pedido.id);
  };

  const possuiEntrega = (pedido) => {
    if (!pedido) return false;
    return pedido.status && pedido.status !== 'PENDENTE';
  };

  function handleDownloadPdf(pedido) {
    if (!contrato) {
      showDefaultModal('error', 'Nenhum contrato selecionado.');
      return;
    }
    if (!pedido) {
      showDefaultModal('error', 'Nenhum pedido selecionado.');
      return;
    }
    const pedidoDTO = {
      id: pedido.id,
      idContrato: pedido.idContrato || contrato.id,
      data: pedido.data,
      idProprio: pedido.idProprio || null,
      usuario: pedido.usuario,
      itens: pedido.itens || []
    };
    setIsGeneratingPdf(true);
    generatePdf({
      startFn: () => axios.post(`${API_BASE_URL.CONTRATO_PEDIDO}/preencherPedidoPdf`, pedidoDTO, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.CONTRATO_PEDIDO}/preencherPedidoPdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.CONTRATO_PEDIDO}/preencherPedidoPdfFile/${jobId}`,
    })
      .then(({ blob }) => {
        const url = window.URL.createObjectURL(blob);
        const pdfId = `pedido_${pedidoDTO.idContrato}_${pedidoDTO.id}_${Date.now()}`;
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Pedido ${pedido.idProprio || pedidoDTO.id} - Contrato ${contrato.numContrato}`, pdfId }));
        window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      })
      .catch(error => {
        console.error('Erro ao gerar PDF:', error);
        showDefaultModal('error', error.message || 'Erro ao gerar o PDF do pedido!');
      })
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDownloadPedidoWord(pedido) {
    if (!contrato) {
      showDefaultModal('error', 'Nenhum contrato selecionado.');
      return;
    }
    if (!pedido) {
      showDefaultModal('error', 'Nenhum pedido selecionado.');
      return;
    }
    const pedidoDTO = {
      id: pedido.id,
      idContrato: pedido.idContrato || contrato.id,
      data: pedido.data,
      idProprio: pedido.idProprio || null,
      usuario: pedido.usuario,
      itens: pedido.itens || []
    };
    setIsGeneratingPdf(true);
    axios
      .post(`${API_BASE_URL.CONTRATO_PEDIDO}/preencherPedidoDocx`, pedidoDTO, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `PEDIDO_${pedido.id}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Erro ao baixar Word:', error);
        showDefaultModal('error', 'Erro ao baixar o documento Word do pedido.');
      })
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDpePdf() {
    setIsGeneratingPdf(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'PEDIDO', idVinculo: contrato.id, idVinculo2: contratoPedidoSelecionado.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          return openPreEmpenhoPdf(res.data.id, res.data.numPreEmpenho);
        } else {
          showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para este pedido.');
        }
      })
      .catch(() => showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para este pedido.'))
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDpeWord() {
    setIsGeneratingPdf(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'PEDIDO', idVinculo: contrato.id, idVinculo2: contratoPedidoSelecionado.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          return openPreEmpenhoWord(res.data.id);
        } else {
          showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para este pedido.');
        }
      })
      .catch(() => showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para este pedido.'))
      .finally(() => setIsGeneratingPdf(false));
  }

  function openPreEmpenhoPdf(id, num) {
    return generatePdf({
      startFn: () => axios.get(`${API_BASE_URL.PRE_EMPENHO}/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfFile/${jobId}`,
    })
      .then(({ blob, missingFields }) => {
        const url = window.URL.createObjectURL(blob);
        const pdfId = `pre_empenho_${id}_${Date.now()}`;
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        if (missingFields && missingFields.length > 0) {
          showDefaultModal('warning', missingFields.join('\n'), () => {
            sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Pré-Empenho ${num || id}`, pdfId }));
            window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
          });
        } else {
          sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Pré-Empenho ${num || id}`, pdfId }));
          window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
        }
      })
      .catch(err => {
        console.error('Erro ao gerar PDF do Pré-Empenho:', err);
        showDefaultModal('error', err.message || 'Erro ao gerar o PDF do Pré-Empenho.');
      });
  }

  function openPreEmpenhoWord(id) {
    return axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/${id}/docx`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const doDownload = () => {
          const link = document.createElement('a');
          link.href = url;
          link.download = `PRE_EMPENHO_${id}.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        };
        const missingHeader = res.headers['x-missing-fields'];
        if (missingHeader) {
          const fields = missingHeader.split(',');
          showDefaultModal('warning', fields.join('\n'), doDownload);
        } else {
          doDownload();
        }
      })
      .catch(err => {
        console.error('Erro ao baixar Word do Pré-Empenho:', err);
        showDefaultModal('error', 'Erro ao gerar o documento Word do Pré-Empenho.');
      });
  }

  function handleAbrirEntrega(pedido) {
    if (!pedido) return;
    const itensConf = (pedido.itens || []).map(item => ({
      idContratoItem: item.idContratoItem,
      descricao: item.contratoItem?.descricao || `Item ${item.idContratoItem}`,
      unidade: item.contratoItem?.unidade || '',
      valorUnit: item.contratoItem?.valorUnit || 0,
      qtdePedida: item.qtdePedida || 0,
      qtdeEntregueAnt: item.qtdeEntregue || 0,
      qtdeEntregueNova: 0,
    }));
    setConfirmacaoItens(itensConf);
    setNfData({ numNotaFiscal: '', serieNotaFiscal: '', dataNotaFiscal: '', valorNotaFiscal: '' });
    setShowEntregaModal(true);
  }

  function handleConfirmarEntrega() {
    const payload = {
      dataConfirmacao: new Date().toISOString().split('T')[0],
      itens: confirmacaoItens.map(i => ({
        idContratoItem: i.idContratoItem,
        qtdeEntregue: i.qtdeEntregueNova,
      })),
      ...nfData.numNotaFiscal && {
        numNotaFiscal: nfData.numNotaFiscal,
        serieNotaFiscal: nfData.serieNotaFiscal,
        dataNotaFiscal: nfData.dataNotaFiscal || null,
        valorNotaFiscal: parseFloat(nfData.valorNotaFiscal.replace(/\./g, '').replace(',', '.')) || 0,
      },
    };
    axios
      .put(
        `${API_BASE_URL.CONTRATO_PEDIDO}/${contrato.id}/${contratoPedidoSelecionado.id}/confirmar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowEntregaModal(false);
        showDefaultModal('success', 'Entrega confirmada com sucesso!');
        setAtualizar(prev => !prev);
        setContratoPedidoSelecionado(null);
      })
      .catch(error => {
        console.error('Erro ao confirmar entrega:', error);
        showDefaultModal('error', 'Erro ao confirmar entrega!');
      });
  }

  function handleItemChange(idx, val) {
    setConfirmacaoItens(prev =>
      prev.map((it, i) => i === idx ? { ...it, qtdeEntregueNova: val } : it)
    );
  }

  function handleConfirmarEntregaTotal() {
    const itensTotal = confirmacaoItens.map(item => ({
      ...item,
      qtdeEntregueNova: item.qtdePedida - item.qtdeEntregueAnt,
    }));
    setConfirmacaoItens(itensTotal);
    const payload = {
      dataConfirmacao: new Date().toISOString().split('T')[0],
      itens: itensTotal.map(i => ({
        idContratoItem: i.idContratoItem,
        qtdeEntregue: i.qtdeEntregueNova,
      })),
      ...nfData.numNotaFiscal && {
        numNotaFiscal: nfData.numNotaFiscal,
        serieNotaFiscal: nfData.serieNotaFiscal,
        dataNotaFiscal: nfData.dataNotaFiscal || null,
        valorNotaFiscal: parseFloat(nfData.valorNotaFiscal.replace(/\./g, '').replace(',', '.')) || 0,
      },
    };
    axios
      .put(
        `${API_BASE_URL.CONTRATO_PEDIDO}/${contrato.id}/${contratoPedidoSelecionado.id}/confirmar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowEntregaModal(false);
        showDefaultModal('success', 'Entrega total confirmada com sucesso!');
        setAtualizar(prev => !prev);
        setContratoPedidoSelecionado(null);
      })
      .catch(error => {
        console.error('Erro ao confirmar entrega total:', error);
        showDefaultModal('error', 'Erro ao confirmar entrega total!');
      });
  }

  function handleAbrirReversao(pedido) {
    if (!pedido) return;
    const itensRev = (pedido.itens || []).map(item => ({
      idItem: item.idContratoItem,
      descricao: item.contratoItem?.descricao || `Item ${item.idContratoItem}`,
      unidade: item.contratoItem?.unidade || '',
      qtdePedida: item.qtdePedida || 0,
      qtdeEntregue: item.qtdeEntregue || 0,
    }));
    setReversaoItens(itensRev);
    setShowReversaoModal(true);
  }

  function handleReverter(payload) {
    setReversando(true);
    axios
      .put(
        `${API_BASE_URL.CONTRATO_PEDIDO}/${contrato.id}/${contratoPedidoSelecionado.id}/reverter`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowReversaoModal(false);
        showDefaultModal('success', 'Entrega revertida com sucesso!');
        setAtualizar(prev => !prev);
        setContratoPedidoSelecionado(null);
      })
      .catch(error => {
        console.error('Erro ao reverter entrega:', error);
        showDefaultModal('error', error.response?.data?.error || 'Erro ao reverter a entrega!');
      })
      .finally(() => setReversando(false));
  }

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.35rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h6 className="modal-title mb-0">
              <i className="bi bi-cart3 me-2"></i>
              Pedidos do Contrato {contrato?.numContrato}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} title="Fechar"></button>
          </div>
          <PageContent>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 mb-2">
              <div className="botao-grid-2col">
                <button
                  className="btn btn-success btn-sm botao-igual"
                  onClick={handleNew}
                  disabled={possuiAditivo}
                  title={possuiAditivo ? 'Bloqueado - pedidos são registrados no aditivo' : 'Cadastrar novo pedido'}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Novo
                </button>
                <button
                  className="btn btn-primary btn-sm botao-igual"
                  disabled={!contratoPedidoSelecionado || possuiAditivo}
                  onClick={() => handleEdit(contratoPedidoSelecionado)}
                  title={possuiAditivo ? 'Bloqueado - pedidos são registrados no aditivo' : (!contratoPedidoSelecionado ? 'Selecione um pedido' : 'Editar pedido selecionado')}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Editar
                </button>
                {paramsEntidade.EDITAR_DPE !== 'N' && (
                  <button
                    className="btn btn-primary btn-sm botao-igual"
                    disabled={!contratoPedidoSelecionado || possuiAditivo}
                    title={possuiAditivo ? 'Bloqueado - pedidos são registrados no aditivo' : 'Editar Pré-Empenho do pedido selecionado'}
                    onClick={() => {
                      axios
                        .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
                          params: { tipoVinculo: 'PEDIDO', idVinculo: contrato.id, idVinculo2: contratoPedidoSelecionado.id },
                          headers: { Authorization: `Bearer ${token}` },
                        })
                        .then(res => {
                          if (res.data && res.data.id) {
                            setPreEmpenhoData(res.data);
                          } else {
                            setPreEmpenhoData({ objeto: contrato?.objeto || '' });
                          }
                          setShowPreEmpenhoModal(true);
                        })
                        .catch(() => {
                          setPreEmpenhoData({ objeto: contrato?.objeto || '' });
                          setShowPreEmpenhoModal(true);
                        });
                    }}
                    title="Editar Pré-Empenho do pedido selecionado"
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Editar DPE
                  </button>
                )}
                <button
                  className="btn btn-danger btn-sm botao-igual"
                  disabled={!contratoPedidoSelecionado || possuiAditivo || possuiPedidoPosterior(contratoPedidoSelecionado) || possuiEntrega(contratoPedidoSelecionado)}
                  onClick={() => handleDelete(contratoPedidoSelecionado)}
                  title={
                    possuiAditivo
                      ? 'Bloqueado - pedidos são registrados no aditivo'
                      : !contratoPedidoSelecionado
                      ? 'Selecione um pedido'
                      : possuiPedidoPosterior(contratoPedidoSelecionado)
                      ? 'Pedido bloqueado - existem pedidos posteriores'
                      : possuiEntrega(contratoPedidoSelecionado)
                      ? 'Pedido bloqueado - já possui entrega registrada'
                      : 'Excluir pedido selecionado'
                  }
                >
                  <i className="bi bi-trash me-1"></i>
                  Excluir
                </button>
                <div className="toolbar-separator" />
                <button
                  className="btn btn-success btn-sm botao-igual"
                  disabled={!contratoPedidoSelecionado}
                  onClick={() => { setContratoPedidoItemSelecionado(contratoPedidoSelecionado); setModalContratoPedidoItensAberto(true); }}
                  title="Ver itens do pedido selecionado"
                >
                  <i className="bi bi-list-check me-1"></i>
                  Itens do Pedido
                </button>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm" disabled={!contratoPedidoSelecionado} className="botao-igual">
                    <i className="bi bi-eye me-1"></i>Visualizar
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">Pedido</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); handleDownloadPdf(contratoPedidoSelecionado); }} title="Abrir PDF do Pedido">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); handleDownloadPedidoWord(contratoPedidoSelecionado); }} title="Baixar Word do Pedido">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <Dropdown.Divider className="my-1" />
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">DPE</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); handleDpePdf(); }} title="Abrir PDF do DPE">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); handleDpeWord(); }} title="Baixar Word do DPE">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <button
                  className="btn btn-success btn-sm botao-igual"
                  disabled={!contratoPedidoSelecionado}
                  onClick={() => handleAbrirEntrega(contratoPedidoSelecionado)}
                  title="Visualizar entregas do pedido selecionado"
                >
                  <i className="bi bi-check2-circle me-1"></i>
                  Entregas
                </button>
                {paramsEntidade.REVERTER_ENTREGA === 'S' && (
                  <button
                    className="btn btn-danger btn-sm botao-igual"
                    disabled={!contratoPedidoSelecionado || !possuiEntrega(contratoPedidoSelecionado)}
                    onClick={() => handleAbrirReversao(contratoPedidoSelecionado)}
                    title={
                      !contratoPedidoSelecionado
                        ? 'Selecione um pedido'
                        : !possuiEntrega(contratoPedidoSelecionado)
                        ? 'Pedido sem entrega registrada'
                        : 'Reverter entrega do pedido selecionado'
                    }
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                    Reverter
                  </button>
                )}
                {isAdmin && (
                  <label
                    className="btn btn-secondary btn-sm botao-igual"
                    title={possuiAditivo ? 'Bloqueado - pedidos são registrados no aditivo' : 'Importar pedidos do contrato via JSON'}
                    style={{ cursor: possuiAditivo ? 'not-allowed' : 'pointer' }}
                  >
                    <i className="bi bi-file-earmark-arrow-up me-1"></i>
                    Import JSON
                    <input
                      type="file"
                      accept=".json,application/json"
                      style={{ display: 'none' }}
                      ref={importInputRef}
                      disabled={!contrato || possuiAditivo}
                    />
                  </label>
                )}
              </div>
              <div className="col-12 col-lg-2 ms-lg-auto">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar..."
                    title="Buscar por número do pedido ou data"
                    value={filtro}
                    onChange={e => setFiltro(e.target.value.toUpperCase())}
                  />
                </div>
              </div>
            </div>

            <LoadingModal loading={isGeneratingPdf} message="Gerando PDF, por favor aguarde..." />
            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2 align-middle">Nº Pedido</th>
                  <th scope="col" className="px-2 align-middle">Data</th>
                  <th scope="col" className="px-2 text-center align-middle">Status</th>
                  <th scope="col" className="px-2 text-end align-middle">Qtd. Itens</th>
                    <th scope="col" className="px-2 text-end align-middle">Valor Total Pedido</th>
                    <th scope="col" className="px-2 text-end">Valor Entregue</th>
                    <th scope="col" className="px-2 text-end">Valor Pendente</th>
                 </tr>}>
                <tbody>
                  {currentItems.map(pedido => {
                      const bloqueado = possuiPedidoPosterior(pedido);
                      return (
                        <tr
                          key={pedido.id}
                          className={
                            contratoPedidoSelecionado &&
                            contratoPedidoSelecionado.id === pedido.id
                              ? 'table-primary'
                              : ''
                          }
                          style={{ cursor: 'pointer' }}
                          onClick={() => setContratoPedidoSelecionado(pedido)}
                        >
                          <td className="px-2">
                            {pedido.idProprio || pedido.id}
                            {bloqueado && (
                              <i
                                className="bi bi-lock-fill ms-2 text-warning"
                                title="Pedido bloqueado - existem pedidos posteriores"
                              ></i>
                            )}
                            {!bloqueado && possuiEntrega(pedido) && (
                              <i
                                className="bi bi-lock-fill ms-2 text-danger"
                                title="Pedido bloqueado - já possui entrega registrada"
                              ></i>
                            )}
                          </td>
                          <td className="px-2">{formatDate(pedido.data)}</td>
                          <td className="px-2 text-center">
                            <span className={`badge ${getBadgeStatus(pedido.status || 'PENDENTE')}`}>
                              {pedido.status || 'PENDENTE'}
                            </span>
                          </td>
                          <td className="px-2 text-end">{pedido.itens?.length || 0}</td>
                          <td className="px-2 text-end">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorTotal(pedido))}
                          </td>
                          <td className="px-2 text-end">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorEntregue(pedido))}
                          </td>
                          <td className="px-2 text-end fw-bold text-primary">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorTotal(pedido) - calcularValorEntregue(pedido))}
                          </td>
                        </tr>
                      );
                    })}
                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-3 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                        Nenhum pedido encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
                  {contratoPedidosFiltrados.length > 0 && (
                  <tfoot className="fw-bold" style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f8f9fa', isolation: 'isolate' }}>
                    <tr>
                      <td colSpan="4" className="px-2 text-end">TOTAL GERAL:</td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeral())}
                      </td>
                      <td className="px-2 text-end text-success">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeralEntregue())}
                      </td>
                      <td className="px-2 text-end fw-bold text-primary">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeralPendente())}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </DataTable>
            </TableContainer>
          </PageContent>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>

      {showModal && (
        <ContratoPedidoModal
          show={showModal}
          contratoPedido={contratoPedidoSelecionado && contratoPedidoSelecionado.id ? contratoPedidoSelecionado : null}
          contrato={contrato}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          dataUltimoPedido={contratoPedidoSelecionado && !contratoPedidoSelecionado.id ? contratoPedidoSelecionado.dataUltimoPedido : null}
        />
      )}

      <ContratoPedidoItensModal
        isOpen={modalContratoPedidoItensAberto}
        onClose={() => { setModalContratoPedidoItensAberto(false); carregarPedidos(contrato.id); }}
        contrato={contrato}
        contratoPedido={contratoPedidoItemSelecionado}
      />

      <ConfirmarEntregaModal
        show={showEntregaModal}
        entityType="Pedido"
        entityId={contratoPedidoSelecionado?.id}
        usuario={contratoPedidoSelecionado?.usuario}
        itens={confirmacaoItens}
        onItemChange={handleItemChange}
        onClose={() => setShowEntregaModal(false)}
        onConfirm={handleConfirmarEntrega}
        onConfirmTotal={handleConfirmarEntregaTotal}
        numNotaFiscal={nfData.numNotaFiscal}
        serieNotaFiscal={nfData.serieNotaFiscal}
        dataNotaFiscal={nfData.dataNotaFiscal}
        valorNotaFiscal={nfData.valorNotaFiscal}
        onNfChange={({ name, value }) => setNfData(prev => ({ ...prev, [name]: value }))}
      />

      <ReverterEntregaModal
        show={showReversaoModal}
        entityType="Pedido"
        entityId={contratoPedidoSelecionado?.id}
        itens={reversaoItens}
        isSaving={isReversando}
        onConfirm={handleReverter}
        onClose={() => setShowReversaoModal(false)}
      />

      <PreEmpenhoModal
        show={showPreEmpenhoModal}
        preEmpenho={preEmpenhoData}
        onClose={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
        onSave={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
      />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

export default ContratoPedidosModal;
